export type MarkerBounds = { left: number; right: number; top: number; bottom: number };
export type MarkerPlacement = { x: number; y: number; radius: number; number: number };
export type ClusteredMarkerPlacement = MarkerPlacement & { itemIds: number[]; numbers: number[] };
export type MarkerTextLayout = {
  lines: string[];
  fontSize: number;
  lineHeight: number;
  width: number;
  height: number;
};

export function hasFiniteCoordinates(latitude: number | null, longitude: number | null): boolean {
  return typeof latitude === 'number' && typeof longitude === 'number'
    && Number.isFinite(latitude) && Number.isFinite(longitude);
}

function overlaps(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export function markerTextLayout(marker: MarkerPlacement & { numbers?: number[] }): MarkerTextLayout {
  const numbers = Array.isArray(marker.numbers) && marker.numbers.length > 0
    ? marker.numbers.slice().sort((a, b) => a - b)
    : [Math.abs(marker.number)];
  const label = numbers.join('/');
  const lines = numbers.length > 1
    ? [numbers.slice(0, 2).join('/'), ...(numbers.length > 2 ? [`/${numbers[2]}`] : [])]
    : [label];
  const longestLineLength = Math.max(1, ...lines.map((line) => line.length));
  // Use almost the full diameter. The small allowance accounts only for the
  // text halo, so the visible glyphs may sit close to the marker outline.
  const availableDiameter = Math.max(1, marker.radius * 2 - 0.45);
  const fontSize = Math.max(1.5, Math.min(
    8.5,
    availableDiameter / (longestLineLength * 0.62),
    availableDiameter / (lines.length * 0.95),
  ));
  const lineHeight = fontSize * 0.95;
  const width = Math.min(availableDiameter, longestLineLength * fontSize * 0.62 + 0.45);
  const height = Math.min(availableDiameter, lines.length * lineHeight + 0.45);
  return { lines, fontSize, lineHeight, width, height };
}

function textBox(marker: MarkerPlacement & { numbers?: number[] }): { x: number; y: number; w: number; h: number } {
  const layout = markerTextLayout(marker);
  return {
    x: marker.x - layout.width / 2,
    y: marker.y - layout.height / 2,
    w: layout.width,
    h: layout.height,
  };
}

function circleBox(marker: MarkerPlacement): { x: number; y: number; w: number; h: number } {
  return { x: marker.x - marker.radius, y: marker.y - marker.radius, w: marker.radius * 2, h: marker.radius * 2 };
}

/**
 * Keep circles free to overlap, but nudge a marker when its number would be
 * covered by an earlier marker. Candidate offsets are deterministic and the
 * first valid position is always the smallest available movement.
 */
export function resolveMarkerTextCollisions<T extends MarkerPlacement & { numbers?: number[] }>(markers: T[], bounds: MarkerBounds): T[] {
  const placed: T[] = [];
  const offsets = [
    [0, 0], [1, 0], [-1, 0], [0, 1], [0, -1],
    [1, 1], [-1, 1], [1, -1], [-1, -1],
    [2, 0], [-2, 0], [0, 2], [0, -2],
    [2, 1], [-2, 1], [2, -1], [-2, -1],
  ] as const;
  markers.forEach((marker) => {
    const step = Math.max(4, marker.radius * 1.8);
    const original = { ...marker } as T;
    const valid = offsets.map(([dx, dy]) => ({
      ...original,
      x: original.x + dx * step,
      y: original.y + dy * step,
    })).find((candidate) => {
      const circle = circleBox(candidate);
      if (circle.x < bounds.left || circle.x + circle.w > bounds.right
        || circle.y < bounds.top || circle.y + circle.h > bounds.bottom) return false;
      const text = textBox(candidate);
      return !placed.some((other) => overlaps(text, circleBox(other)) || overlaps(text, textBox(other)));
    });
    placed.push((valid ?? original) as T);
  });
  return placed;
}

/** Group only genuinely coincident/nearby markers; each visible marker holds
 * at most three project numbers so dense areas stay readable and compact. */
export function clusterMarkerPlacements<T extends MarkerPlacement & { itemId: number }>(
  markers: T[], maxPerCluster = 3, proximity = 14,
): Array<ClusteredMarkerPlacement & Omit<T, 'number' | 'itemId'>> {
  const sorted = [...markers].sort((a, b) => a.y - b.y || a.x - b.x || a.itemId - b.itemId);
  const clusters: Array<{ members: T[]; x: number; y: number }> = [];
  sorted.forEach((marker) => {
    const cluster = clusters.find((candidate) => candidate.members.length < maxPerCluster
      && Math.hypot(candidate.x - marker.x, candidate.y - marker.y) <= proximity);
    if (cluster) {
      cluster.members.push(marker);
      cluster.x = cluster.members.reduce((sum, member) => sum + member.x, 0) / cluster.members.length;
      cluster.y = cluster.members.reduce((sum, member) => sum + member.y, 0) / cluster.members.length;
    } else clusters.push({ members: [marker], x: marker.x, y: marker.y });
  });
  return clusters.map(({ members, x, y }) => {
    const first = members[0];
    return {
      ...first,
      x,
      y,
      radius: members.length === 1 ? first.radius : Math.min(11, first.radius + members.length),
      number: first.number,
      itemIds: members.map((member) => member.itemId),
      // Marker labels are presentation-only; always show project numbers in
      // ascending order regardless of coordinate/cluster insertion order.
      numbers: members.map((member) => member.number).sort((a, b) => a - b),
    } as ClusteredMarkerPlacement & Omit<T, 'number' | 'itemId'>;
  });
}
